import { API_MAP } from 'helpers';
import * as Rx from 'rxjs';
import type { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import type { BadgeProps, ContentNew, BannerPub, IBanner } from 'v2/interfaces/settings';
import type { IUserData } from 'v2/interfaces/user';
import apiService from 'v2/services/api';

import cacheService from './cache';
import eduzzAccountsService from './eduzzAccount';
import type { EduzzAccountsService } from './eduzzAccount';

/**
 * Service responsible for controlling `Settings` component.
 *
 * @class SettingsService
 */
class SettingsService {
  /**
   * @method getBanner
   * @return {Observable} an `IBanner`
   * returned by the API
   */
  private currentUser: IUserData;

  constructor(private eduzzAccountsService: EduzzAccountsService) {
    this.eduzzAccountsService.getUser().subscribe(user => {
      this.currentUser = user;
    });
  }

  public getBanner = (module: string): Observable<IBanner> => {
    return apiService.get('/orbita_settings/banner', { module }).pipe(map(response => response.data));
  };

  public getBanners = (): Observable<BannerPub[]> => {
    const cacheKey = `banners-${this.currentUser.id}`;

    return cacheService.getData(cacheKey).pipe(
      switchMap(cache => {
        if (!cache || cacheService.isExpirated(cache)) {
          return apiService.get('/banners/self').pipe(
            map(response => response.data),
            switchMap(banners => cacheService.saveData(cacheKey, banners, { persist: true, expirationMinutes: 60 })),
            map(cache => cache.data)
          );
        }

        return Rx.of(cache.data);
      })
    );
  };

  public getNews = (): Observable<ContentNew[]> => {
    const cacheKey = `news-modals-${this.currentUser.id}`;

    return cacheService.getData(cacheKey).pipe(
      switchMap(cache => {
        if (!cache || cacheService.isExpirated(cache)) {
          return apiService.get('/modals/self').pipe(
            map(response => response.data),
            switchMap(newsModals =>
              cacheService.saveData(cacheKey, newsModals, { persist: true, expirationMinutes: 60 })
            ),
            map(cache => cache.data)
          );
        }

        return Rx.of(cache.data);
      })
    );
  };

  public actualUserBadge = (customerId: any): Observable<BadgeProps> => {
    return apiService
      .get(`${API_MAP.customerBadge}`.replace('{customerId}', customerId))
      .pipe(map(response => response.data));
  };
}

const SettingsServiceFactory = new SettingsService(eduzzAccountsService);
export const settingsFactory = SettingsServiceFactory;
